import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { Component, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { Subject, finalize, first } from 'rxjs';
import { I18N_NAMESPACE } from '../../../internal/constants';
import type { OnyxBadge } from '../../badges/interfaces';
import { OnyxBadgeComponent } from '../../badges/onyx-badge/onyx-badge.component';
import { OnyxButtonComponent } from '../../buttons/onyx-button/onyx-button.component';
import { OnyxModalComponent } from '../onyx-modal/onyx-modal.component';

export interface OnyxConfirmModalData {
  heading: string;
  showClose?: boolean;
  badges?: OnyxBadge<string>[];
  message?: string;
  templateRef?: TemplateRef<any>;
  cancelButtonText?: string;
  confirmButtonText: string;
  confirmButtonColor?: 'blue' | 'green' | 'red' | 'violet' | 'black' | 'orange';
  action?: () => Observable<unknown>;
}

@Component({
  selector: 'onyx-confirm-modal',
  imports: [
    OnyxModalComponent,
    NgTemplateOutlet,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxBadgeComponent,
  ],
  templateUrl: './onyx-confirm-modal.component.html',
  styleUrl: './onyx-confirm-modal.component.scss',
})
export class OnyxConfirmModalComponent {
  protected readonly data = inject<OnyxConfirmModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<boolean>);

  protected readonly I18N = I18N_NAMESPACE;

  protected close$ = new Subject<boolean | undefined>();
  protected loading = signal(false);

  protected confirm(): void {
    const action = this.data.action;
    if (!action) return this.close$.next(true);
    if (this.loading()) return;

    this.loading.set(true);
    action()
      .pipe(
        first(),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({ complete: () => this.close$.next(true), error: () => null });
  }
}
